import { InstagramLogo, TikTokLogo, YouTubeLogo } from '@/shared/icons/index';
import { ConstMap } from '@/shared/utils/const-map';
import { countries, languages } from 'countries-list';
import type { ElementType } from 'react';

type SelectOption<T> = {
  value: T;
  label: string;
  icon?: ElementType;
  order?: number;
  query_disabled?: boolean;
  creator_search_disabled?: boolean;
};

export enum PUBLIC_FILTER_TYPE {
  KEYWORD = 'Keyword',
  PROMOTION_TYPE = 'Promotion type',
  PLATFORM = 'Platform',
  GENDER = 'Gender',
  RACE = 'Race',
  AGE = 'Age',
  LOCATION = 'Location',
  LANGUAGE = 'Language',
}

export enum AGE {
  KIDS = 'kids',
  TEENS = 'teens',
  TWENTIES = 'twenties',
  THIRTIES = 'thirties',
  FORTIES = 'fourties',
  FIFTIES = 'fifties',
  SIXTIES = 'sixties',
  SENIORS = 'seniors',
  UNKNOWN = 'unknown',
}

export enum GENDER {
  MALE = 'Male',
  FEMALE = 'Female',
}

export enum RACE {
  WHITE = 'White',
  HISPANIC = 'Hispanic',
  BLACK = 'Black',
  EAST_ASIAN = 'East asian',
  MIDDLE_EASTERN = 'Middle eastern',
  SOUTH_ASIAN = 'South asian',
  SOUTH_EAST_ASIAN = 'Southeast asian',
  OTHERS = 'Others',
}

export enum SOURCE_PLATFORM_TYPE {
  TIKTOK = 'tiktok',
  INSTAGRAM = 'instagram',
  YOUTUBE = 'youtube',
}

export enum CONTENT_TYPE {
  ALL = 0,
  SPONSORED = 1,
  ORGANIC = 2,
}

export enum MATCHED_TYPE {
  ALL_MATCHES = 'all_matchs',
  FULL_MATCHED = 'full_match',
  PARTIAL_MATCHED = 'partial_match',
}

export const countriesList = Object.entries(countries).map(([code, country]) => ({
  value: code,
  label: country.name,
}));

export const languageList = Object.entries(languages).map(([code, language]) => ({
  value: code,
  label: language.name,
}));

export const getPlatformLogo = (platform: SOURCE_PLATFORM_TYPE): React.ElementType => {
  switch (platform) {
    case SOURCE_PLATFORM_TYPE.TIKTOK:
      return TikTokLogo;
    case SOURCE_PLATFORM_TYPE.INSTAGRAM:
      return InstagramLogo;
    case SOURCE_PLATFORM_TYPE.YOUTUBE:
      return YouTubeLogo;
    default:
      throw new Error(`Invalid platform: ${platform}`);
  }
};

export const getPlatformName = (platform: SOURCE_PLATFORM_TYPE): string => {
  switch (platform) {
    case SOURCE_PLATFORM_TYPE.TIKTOK:
      return 'TikTok';
    case SOURCE_PLATFORM_TYPE.INSTAGRAM:
      return 'Instagram';
    case SOURCE_PLATFORM_TYPE.YOUTUBE:
      return 'YouTube';
    default:
      throw new Error(`Invalid platform: ${platform}`);
  }
};

export const platformColorPalette = {
  [SOURCE_PLATFORM_TYPE.TIKTOK]: 'var(--chart-1)',
  [SOURCE_PLATFORM_TYPE.INSTAGRAM]: 'var(--chart-2)',
  [SOURCE_PLATFORM_TYPE.YOUTUBE]: 'var(--chart-4)',
};

export const platformOptions: SelectOption<SOURCE_PLATFORM_TYPE>[] = [
  {
    value: SOURCE_PLATFORM_TYPE.TIKTOK,
    icon: getPlatformLogo(SOURCE_PLATFORM_TYPE.TIKTOK),
    label: getPlatformName(SOURCE_PLATFORM_TYPE.TIKTOK),
    order: 1,
  },
  {
    value: SOURCE_PLATFORM_TYPE.INSTAGRAM,
    icon: getPlatformLogo(SOURCE_PLATFORM_TYPE.INSTAGRAM),
    label: getPlatformName(SOURCE_PLATFORM_TYPE.INSTAGRAM),
    order: 2,
    creator_search_disabled: true,
  },
  {
    value: SOURCE_PLATFORM_TYPE.YOUTUBE,
    icon: getPlatformLogo(SOURCE_PLATFORM_TYPE.YOUTUBE),
    label: getPlatformName(SOURCE_PLATFORM_TYPE.YOUTUBE),
    order: 3,
    creator_search_disabled: true,
  },
];

export const contentTypeOptions: SelectOption<CONTENT_TYPE>[] = [
  { value: CONTENT_TYPE.SPONSORED, label: 'Sponsored' },
  { value: CONTENT_TYPE.ORGANIC, label: 'Organic' },
];

interface GetFilterOptionsMap {
  (type: PUBLIC_FILTER_TYPE.PROMOTION_TYPE): CONTENT_TYPE[];
  (type: PUBLIC_FILTER_TYPE.PLATFORM): SOURCE_PLATFORM_TYPE[];
  (type: PUBLIC_FILTER_TYPE.GENDER): GENDER[];
  (type: PUBLIC_FILTER_TYPE.RACE): RACE[];
  (type: PUBLIC_FILTER_TYPE.AGE): AGE[];
  (type: PUBLIC_FILTER_TYPE.LOCATION): string[];
  (type: PUBLIC_FILTER_TYPE.LANGUAGE): string[];
  (type: PUBLIC_FILTER_TYPE): string[];
}

export const getFilterOptionsMap = ((type: PUBLIC_FILTER_TYPE) => {
  if (type === PUBLIC_FILTER_TYPE.PROMOTION_TYPE)
    return [CONTENT_TYPE.SPONSORED, CONTENT_TYPE.ORGANIC];
  if (type === PUBLIC_FILTER_TYPE.PLATFORM)
    return [
      SOURCE_PLATFORM_TYPE.TIKTOK,
      SOURCE_PLATFORM_TYPE.INSTAGRAM,
      SOURCE_PLATFORM_TYPE.YOUTUBE,
    ];
  if (type === PUBLIC_FILTER_TYPE.GENDER) return [GENDER.MALE, GENDER.FEMALE];
  if (type === PUBLIC_FILTER_TYPE.RACE)
    return [
      RACE.WHITE,
      RACE.BLACK,
      RACE.HISPANIC,
      RACE.EAST_ASIAN,
      RACE.MIDDLE_EASTERN,
      RACE.SOUTH_ASIAN,
      RACE.SOUTH_EAST_ASIAN,
      RACE.OTHERS,
    ];
  if (type === PUBLIC_FILTER_TYPE.AGE)
    return [
      AGE.KIDS,
      AGE.TEENS,
      AGE.TWENTIES,
      AGE.THIRTIES,
      AGE.FORTIES,
      AGE.FIFTIES,
      AGE.SIXTIES,
      AGE.SENIORS,
    ];
  if (type === PUBLIC_FILTER_TYPE.LOCATION) return countriesList.map((country) => country.value);
  if (type === PUBLIC_FILTER_TYPE.LANGUAGE) return languageList.map((language) => language.value);
  throw new Error(`Invalid filter type: ${type}`);
}) as GetFilterOptionsMap;

export const AGE_MAP = new ConstMap<AGE, string>([
  [AGE.KIDS, '1-9'],
  [AGE.TEENS, '10-19'],
  [AGE.TWENTIES, '20-29'],
  [AGE.THIRTIES, '30-39'],
  [AGE.FORTIES, '40-49'],
  [AGE.FIFTIES, '50-59'],
  [AGE.SIXTIES, '60-69'],
  [AGE.SENIORS, '70+'],
]);

export const GENDER_MAP = new ConstMap<GENDER, string>([
  [GENDER.MALE, 'Male'],
  [GENDER.FEMALE, 'Female'],
]);

export const RACE_MAP = new ConstMap<RACE, string>([
  [RACE.WHITE, 'White'],
  [RACE.HISPANIC, 'Hispanic'],
  [RACE.BLACK, 'Black'],
  [RACE.EAST_ASIAN, 'East asian'],
  [RACE.MIDDLE_EASTERN, 'Middle eastern'],
  [RACE.SOUTH_ASIAN, 'South asian'],
  [RACE.SOUTH_EAST_ASIAN, 'Southeast asian'],
  [RACE.OTHERS, 'Others'],
]);
